/**
 * What one undo step says it undid (`observers-page-camera-stage`, FR16): *Undone: {line}*.
 * Wording follows the v2 design's change lines (*New observer on X*, *Observer deleted from X*).
 */
export type ObserverChange =
  | { kind: 'new'; entity: string }
  | { kind: 'deleted'; entity: string }
  | { kind: 'reshaped'; entity: string }
  | { kind: 'trackSide'; entity: string }
  | { kind: 'linked'; entity: string }
  | { kind: 'linkedToCamera'; entity: string; camera: string }
  | { kind: 'removedCamera'; entity: string; camera: string }
  /** A suggestion used, adjusted or re-linked (`observer-autogen-review-walkthrough`). */
  | { kind: 'suggestionUsed'; entity: string }
  /** *Accept all confident* — one step for the batch. */
  | { kind: 'suggestionsAccepted'; count: number };

export const changeLine = (change: ObserverChange): string => {
  switch (change.kind) {
    case 'new':
      return `New observer on ${change.entity}`;
    case 'deleted':
      return `Observer deleted from ${change.entity}`;
    case 'reshaped':
      return `${change.entity} · region changed`;
    case 'trackSide':
      return `${change.entity} · body part changed`;
    case 'linked':
      return `Region linked to ${change.entity}`;
    case 'linkedToCamera':
      return `${change.entity} linked to ${change.camera}`;
    case 'removedCamera':
      return `${change.camera} removed from ${change.entity}`;
    case 'suggestionUsed':
      return `Suggestion used for ${change.entity}`;
    case 'suggestionsAccepted':
      return `${change.count} confident suggestion${change.count === 1 ? '' : 's'} accepted`;
  }
};

/**
 * A live region's next text. The same words twice in a row gain a trailing no-break space, so the
 * region's text still changes and a screen reader reads them again. Only `''` clears.
 */
export const nextAnnouncement = (prev: string, text: string): string =>
  text !== '' && prev === text ? `${text} ` : text;
